import { create } from 'zustand';
import { api, ApiClientError, type Account, type Category, type Dashboard, type MeResponse, type Preferences, type User } from '../lib/api.js';

export interface Toast {
  id: number;
  message: string;
  tone?: 'info' | 'positive' | 'danger';
  action?: { label: string; run: () => void };
}

interface AppState {
  ready: boolean;
  authenticated: boolean;
  user: User | null;
  preferences: Preferences | null;
  providers: { google: boolean; devLogin: boolean };

  accounts: Account[];
  categories: Category[];
  dashboard: Dashboard | null;

  loadingDashboard: boolean;
  error: string | null;
  toasts: Toast[];

  boot: () => Promise<void>;
  refreshDashboard: () => Promise<void>;
  refreshAccounts: () => Promise<void>;
  setPreferences: (patch: Partial<Preferences>) => Promise<void>;
  updateUser: (patch: Partial<{ name: string; currency: string; timezone: string; onboarded: boolean }>) => Promise<void>;
  signOut: () => Promise<void>;
  toast: (message: string, options?: Omit<Toast, 'id' | 'message'>) => void;
  dismissToast: (id: number) => void;
  categoryName: (id: string | null | undefined) => string;
  categorySlug: (id: string | null | undefined) => string;
}

let toastSeq = 0;

export const useApp = create<AppState>((set, get) => ({
  ready: false,
  authenticated: false,
  user: null,
  preferences: null,
  providers: { google: false, devLogin: false },
  accounts: [],
  categories: [],
  dashboard: null,
  loadingDashboard: false,
  error: null,
  toasts: [],

  boot: async () => {
    try {
      const status = await api.auth.status();
      set({ authenticated: status.authenticated, user: status.user, providers: status.providers });
      if (!status.authenticated) { set({ ready: true }); return; }

      const [me, categories, accounts] = await Promise.all([
        api.me.get(), api.categories.list(), api.accounts.list(),
      ]);
      set({
        user: (me as MeResponse).user,
        preferences: (me as MeResponse).preferences,
        categories, accounts, ready: true,
      });
      applyTheme((me as MeResponse).preferences.theme);
      void get().refreshDashboard();
    } catch (error) {
      set({ ready: true, error: message(error) });
    }
  },

  refreshDashboard: async () => {
    set({ loadingDashboard: true });
    try {
      const dashboard = await api.dashboard.get();
      set({ dashboard, loadingDashboard: false, error: null });
    } catch (error) {
      set({ loadingDashboard: false, error: message(error) });
    }
  },

  refreshAccounts: async () => {
    try { set({ accounts: await api.accounts.list() }); }
    catch (error) { set({ error: message(error) }); }
  },

  setPreferences: async (patch) => {
    const previous = get().preferences;
    set({ preferences: previous ? { ...previous, ...patch } : previous });   // optimistic §129
    if (patch.theme) applyTheme(patch.theme);
    try {
      const { preferences } = await api.me.preferences(patch);
      set({ preferences });
      void get().refreshDashboard();
    } catch (error) {
      set({ preferences: previous });                                        // reconcile with the server
      get().toast(message(error), { tone: 'danger' });
    }
  },

  updateUser: async (patch) => {
    try {
      const { user } = await api.me.update(patch);
      set({ user });
    } catch (error) {
      get().toast(message(error), { tone: 'danger' });
    }
  },

  signOut: async () => {
    await api.auth.logout().catch(() => undefined);
    set({ authenticated: false, user: null, dashboard: null, accounts: [] });
  },

  toast: (message, options = {}) => {
    const id = ++toastSeq;
    set({ toasts: [...get().toasts, { id, message, ...options }] });
    setTimeout(() => get().dismissToast(id), 5200);
  },

  dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  categoryName: (id) => {
    if (!id) return 'ללא קטגוריה';
    const category = get().categories.find((c) => c.id === id);
    return category ? category.nameHe ?? category.name : 'ללא קטגוריה';
  },

  categorySlug: (id) => {
    if (!id) return 'uncategorized';
    return get().categories.find((c) => c.id === id)?.slug ?? 'uncategorized';
  },
}));

export function applyTheme(theme: Preferences['theme']): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

function message(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  return 'משהו השתבש. נסה שוב.';
}
